import {
  createStyles,
  Drawer,
  makeStyles,
  Theme,
  Button,
  AppBar,
  Link
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  BORDER_COLOR,
  DEFAULT_WHITE_COLOR,
  APP_PRIMARY_COLOR
} from 'src/style/variable';
import InvoicesStyledTabs from '@components/system/Tabs/InvoicesStyledTabs';
import InvoicesTab from '@components/system/Tabs/InvoicesTab';
import AgGridTable from '@components/system/AgGridTable/AgGridTable';
import './ClaimDetailsStyles.scss';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {getClaimDetailsByID} from '@shared/services/ClaimsServices';
import {formatApiMessage} from '@shared/utils/formatApiMessage';
import {useSelector} from 'react-redux';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import HelperUtils from '@shared/utils/HelperUtils';
import {getStatusLabel} from '../ClaimsAnalysis/ClaimsAnalysis';
import Loader from '@components/framework/loader/Loader';
import ReloadIcon from '@components/system/ReloadIcon/ReloadIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawer: {
      maxWidth: '900px',
      overflowX: 'auto',
      '& .MuiBackdrop-root': {
        backgroundColor: 'transparent'
      },
      '& .MuiDrawer-paperAnchorRight': {
        '@media (max-width:1024px)': {
          width: '100%'
        },
        '@media (max-width:1336px)': {
          width: '100%'
        },
        '@media (max-width:1920px)': {
          width: '100%'
        },
        backgroundColor: DEFAULT_WHITE_COLOR,
        zIndex: 1300,
        borderLeft: `1px solid ${BORDER_COLOR}`,
        overflowX: 'auto',
        marginTop: theme.mixins.toolbar.minHeight,
        width: '95%'
      },
      '& .MuiPaper-elevation16': {
        boxShadow: 'none'
      }
    },
    openDrawerMainDiv: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1
    },
    DrawerHeader: {
      fontSize: '1.5rem',
      padding: '1rem 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      zIndex: 100,
      top: '0',
      backgroundColor: 'white',
      borderBottom: '1px solid lightgray'
    },
    container: {
      display: 'flex',
      flexDirection: 'column'
    },
    section: {
      width: '100%'
    },
    title: {
      margin: '1.5rem 0',
      fontSize: '1.3rem',
      fontWeight: 600
    },
    moreFilters: {
      height: '2.3rem',
      width: '5rem',
      marginLeft: '0.5rem',
      lineHeight: '1rem',
      borderColor: APP_PRIMARY_COLOR,
      color: DEFAULT_WHITE_COLOR,
      fontSize: '1rem',
      backgroundColor: APP_PRIMARY_COLOR,
      '&:hover': {
        backgroundColor: APP_PRIMARY_COLOR
      }
    },
    accountBalanceSection: {
      display: 'flex',
      alignItems: 'flex-start',
      width: '100%',
      borderRadius: '4px',
      boxShadow:
        '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
      padding: '1rem'
    },
    accountItem: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '1.5rem'
    },
    leftAccountDetails: {
      width: '50%'
    },
    rightAccountDetails: {
      width: '50%'
    },
    label: {
      fontSize: '1rem',
      fontWeight: 600,
      width: '30%'
    },
    value: {
      fontSize: '1rem',
      marginLeft: '1rem',
      width: '70%'
    },
    subTitle: {
      fontSize: '1.2rem',
      margin: '1.5rem 0'
    },
    prepaySummaryGrid: {},
    invoicesTopBarMainView: {
      // flex: 1,
      display: 'flex',
      position: 'relative',
      flexDirection: 'row',
      // backgroundColor: 'white',
      // paddingLeft: '2.3rem',
      // paddingRight: '2.3rem',
      padding: 0,
      border: 'none',
      // boxShadow:
      //   '0px 0px 0px -1px rgba(0,0,0,0.2), 0px 2px 6px 0px rgba(0,0,0,0.14), 0px 1px 26px 0px rgba(0,0,0,0.12)',
      '& .MuiTabScrollButton-root': {
        color: APP_PRIMARY_COLOR
      },
      '& .MuiTabs-scroller': {
        // height: 'fit-content'
      },
      background: '#FFFFFF 0% 0% no-repeat padding-box',
      boxShadow: '0px 0px 6px #00000014 !important'
    },
    tabBarRightSection: {
      marginLeft: 'auto',
      display: 'flex',
      marginRight: '1.2rem',
      alignItems: 'center'
    },
    appBar: {
      zIndex: 1,
      boxShadow: 'none',
      [theme.breakpoints.up('md')]: {
        width: `100%`
      }
    },
    linkWrapper: {
      paddingLeft: '1rem'
    },
    linksParentwrapper: {
      display: 'flex',
      alignItems: 'baseline'
    }
  })
);
const getStatusClass = (status: string) => {
  switch (status) {
    case 'pending':
      return 'status-pending'; // Define this in your CSS
    case 'paid':
      return 'status-paid'; // Define this in your CSS
    case 'rejected':
      return 'status-rejected'; // Define this in your CSS
    case 'reversed':
      return 'status-reversed'; // Define this in your CSS
    case 'ignored':
      return 'status-ignored'; // Define this in your CSS
    case 'duplicated':
      return 'status-duplicated'; // Define this in your CSS
    default:
      return '';
  }
};
const ClaimDetails = (props: any): React.ReactElement => {
  const classes = useStyles();
  const {enqueueSnackbar} = useSnackbar();
  const {
    handleClose,
    open,
    claim,
    onReSubmit,
    loader,
    setLoader,
    IgnoreFn
  } = props;
  const groupId: any = useSelector((state: any) => state.auth.groupId);
  const acOfClinics: any = useSelector(
    (state: any) => state.clinics.selectedClinicId || []
  );
  const [claimDetails, setClaimDetails]: any = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {t} = useTranslation();
  const [tab, setTab]: any = useState<any>(0);
  const [historyResponseLine, setHistoryResponseLine] = useState([]);
  const [
    historyResponseSubmittedDate,
    setHistoryResponseSubmittedDate
  ] = useState('');
  const [historyViewMode, setHistoryViewMode] = useState<
    'request' | 'response'
  >('response');
  const [selectedD0String, setSelectedD0String] = useState<string>('');
  const tabs = [
    {
      label: 'Claim Details'
    }
  ];
  const onCloseDrawer = () => {
    // CLEAR ALL SECTIONS HERE...
    handleClose();
  };

  const getHistoryResponse = (dataValue: any) => {
    setHistoryResponseLine(dataValue?.output_data || []);
    setHistoryResponseSubmittedDate(dataValue?.created_at || '');
    setHistoryViewMode('response');
    setSelectedD0String(dataValue?.response_d0_string ?? '');
  };

  const getHistoryRequest = (dataValue: any) => {
    setHistoryResponseLine(dataValue?.input_data || []);
    setHistoryResponseSubmittedDate(dataValue?.created_at || '');
    setHistoryViewMode('request');
    setSelectedD0String(dataValue?.request_d0_string ?? '');
  };

  const fetchClaimDetails = async () => {
    setIsRefreshing(true);
    try {
      const res: any = await getClaimDetailsByID(
        groupId,
        acOfClinics,
        claim?.id
      );
      if (res?.data) {
        setClaimDetails(res?.data);
        if (res.data.history?.[0]) {
          getHistoryResponse(res.data.history[0]);
        } else {
          setSelectedD0String('');
        }
      }
      // return res;
    } catch (err: unknown) {
      enqueueSnackbar(formatApiMessage(err), SnackBarConfig('e'));
    } finally {
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    fetchClaimDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const excludedStatus: string[] = [
    'ignored',
    'closed',
    'adjusted_and_closed',
    'added_to_patient_balance',
    'missing_insurance'
  ];

  const renderIcdAndCptSection = () => {
    return (
      <div className={[classes.section, classes.openDrawerMainDiv].join(' ')}>
        <div className="top-section">
          <div className="section-1">
            <div className="header">{t('Claim Lines')}</div>
            <div className="claim-lines">
              <AgGridTable
                columns={[
                  {
                    headerName: 'NDC Code',
                    field: 'ndc_code',
                    flex: 0.4,
                    cellRenderer: (rowData: any) => {
                      return <div>{rowData?.data?.ndc_code || '-'}</div>;
                    }
                  },
                  {
                    headerName: 'Unit Price',
                    field: 'unit_price',
                    flex: 0.3,
                    cellRenderer: (rowData: any) => {
                      return (
                        <CurrencyFormatter
                          value={rowData?.data?.unit_price || '-'}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'$'}
                          ignoreRound={true}
                          useSign={true}
                        />
                      );
                    }
                  },
                  {
                    headerName: 'Quantity',
                    field: 'quantity',
                    flex: 0.3,
                    cellRenderer: (rowData: any) => {
                      return <div>{rowData?.data?.quantity || '-'}</div>;
                    }
                  },
                  {
                    headerName: 'Total Amount',
                    field: 'line_amount',
                    flex: 0.4,
                    cellRenderer: (rowData: any) => {
                      return (
                        <CurrencyFormatter
                          value={rowData?.data?.line_amount || '-'}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'$'}
                          ignoreRound={true}
                          useSign={true}
                        />
                      );
                    }
                  },
                  {
                    headerName: 'Description',
                    field: 'description',
                    flex: 0.5,
                    cellRenderer: (rowData: any) => {
                      return <div>{rowData?.data?.description || '-'}</div>;
                    }
                  }
                ]}
                data={claimDetails?.claim_lines || []}
              />
            </div>
          </div>
          <div className="section-2">
            <div className="header">{t('Claim Submission History')}</div>
            <div className="claim-history">
              <AgGridTable
                columns={[
                  {
                    headerName: 'Status',
                    field: 'status',
                    flex: 0.5,
                    cellRenderer: (rowData: any) => {
                      return (
                        <div>
                          {getStatusLabel(rowData?.data?.status) || '-'}
                        </div>
                      );
                    }
                  },
                  {
                    headerName: 'Date',
                    field: 'created_at',
                    flex: 0.5,
                    cellRenderer: (rowData: any) => {
                      return (
                        <div>
                          {HelperUtils.formatDisplayDateTime(
                            rowData?.data?.created_at
                          ) || '-'}
                        </div>
                      );
                    }
                  },
                  {
                    headerName: 'Actions',
                    field: 'action',
                    flex: 0.5,
                    cellRenderer: (rowData: any) => {
                      const status: string = rowData?.data?.status;
                      if (
                        typeof status === 'string' &&
                        excludedStatus.includes(status)
                      ) {
                        return <div>-</div>;
                      } else {
                        return (
                          <div style={{lineHeight: 2.8}}>
                            <span
                              className="link-text-style"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                e.preventDefault();
                                getHistoryRequest(rowData?.data);
                              }}>
                              {t('View Request')}
                            </span>
                            <span style={{marginLeft: '0.5rem'}}>|</span>
                            <span
                              className="link-text-style"
                              style={{marginLeft: '0.5rem'}}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                e.preventDefault();
                                getHistoryResponse(rowData?.data);
                              }}>
                              {t('View Response')}
                            </span>
                          </div>
                        );
                      }
                    }
                  }
                ]}
                data={claimDetails?.history || []}
              />
            </div>
          </div>
        </div>
        {historyResponseLine?.length ? (
          <div className="claim-responses-table">
            <div className="claim-response-header">
              {historyViewMode === 'request'
                ? `Claim Request (${HelperUtils.formatDisplayDateTime(
                    historyResponseSubmittedDate
                  )})`
                : `Claim Response (${HelperUtils.formatDisplayDateTime(
                    historyResponseSubmittedDate
                  )})`}
            </div>
            <div className="claim-response-content">
              <div className="claim-response-grid">
                <AgGridTable
                  columns={[
                    {
                      headerName: 'Title',
                      field: 'title',
                      flex: 0.5,
                      cellRenderer: (rowData: any) => {
                        return <div>{rowData?.data?.label || '-'}</div>;
                      }
                    },
                    {
                      headerName:
                        historyViewMode === 'request' ? 'Request' : 'Response',
                      field: 'title',
                      flex: 0.5,
                      cellRenderer: (rowData: any) => {
                        return <div>{rowData?.data?.value ?? '-'}</div>;
                      }
                    }
                  ]}
                  data={historyResponseLine || []}
                />
              </div>
              <div className="claim-response-d0-string">
                <div className="claim-response-d0-string-header">
                  {historyViewMode === 'request'
                    ? t('Request D0 String')
                    : t('Response D0 String')}
                </div>
                <pre className="claim-response-d0-string-value">
                  {selectedD0String || '-'}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    );
  };

  const handleTabChange = (newValue: number): void => {
    setTab(newValue);
  };
  const renderLoader = () => {
    return <Loader size={16} />;
  };

  const renderTabsDetails = (): React.ReactElement => {
    return (
      <AppBar
        className={[classes.invoicesTopBarMainView, classes.appBar].join(' ')}>
        <InvoicesStyledTabs
          value={tab}
          variant="scrollable"
          scrollButtons={'auto'}
          onChange={(event: any, newValue: any) => {
            handleTabChange(newValue);
          }}
          aria-label="top app tabs">
          {tabs.map((item: any) => {
            return <InvoicesTab label={item.label} key={item.label} />;
          })}
        </InvoicesStyledTabs>
        <div className={classes.tabBarRightSection}>
          <div className={classes.linksParentwrapper}>
            <div className="claim-section">
              <div className="title">{'Claim No: '}</div>
              <div className="value">
                {claimDetails?.prescription_reference_number || '-'}
              </div>
            </div>
            <div className={classes.linkWrapper}>
              {claimDetails?.status === 'rejected' ? (
                <div className="claim-section">
                  <Link
                    style={{padding: '0.4rem'}}
                    component="button"
                    onClick={() => {
                      onReSubmit(claimDetails);
                    }}>
                    {' '}
                    {loader.name ===
                      t(
                        'CLAIMS.CLAIM_ANALYSIS.ACTIONS.MARK_FOR_RE_SUBMISSION'
                      ) && loader.number === claimDetails?.id
                      ? renderLoader()
                      : t(
                          'CLAIMS.CLAIM_ANALYSIS.ACTIONS.MARK_FOR_RE_SUBMISSION'
                        )}
                  </Link>
                  <Link
                    style={{padding: '0.4rem'}}
                    component="button"
                    onClick={() => {
                      setLoader((p: any) => ({
                        ...p,
                        number: claimDetails?.id,
                        name: t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.IGNORE')
                      }));
                      IgnoreFn(claimDetails?.id, true);
                    }}>
                    {loader.name ===
                      t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.IGNORE') &&
                    loader.number === claimDetails?.id
                      ? renderLoader()
                      : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.IGNORE')}
                  </Link>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
          <ReloadIcon
            title={t('DUE_INVOICES.REFRESH_DATA')}
            onClick={fetchClaimDetails}
            disabled={isRefreshing}
            fontSize="large"
            dataTestId="pcl-pharmacy-claim-details-refresh"
          />
          <Button
            color="primary"
            variant="outlined"
            className={classes.moreFilters}
            onClick={onCloseDrawer}>
            {t('COMMON.CLOSE')}
          </Button>
        </div>
      </AppBar>
    ); // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const renderDrawer = (): React.ReactElement => {
    return (
      <Drawer
        className={classes.drawer}
        anchor={'right'}
        open={open}
        onClose={onCloseDrawer}>
        {renderTabsDetails()}
        <div className="claim-details-parent">
          <div className="info-section-wrapper">
            <div className="info-section">
              <div className="claim-no-section">
                <div className="title">{'Payer Name:'}</div>
                <div className="value">{claimDetails?.resp_party || '-'},</div>
              </div>
              <div className="claim-no-section">
                <div className="title">{'Service Date:'}</div>
                <div className="value">
                  {HelperUtils.formatDisplayDate(
                    claimDetails?.date_of_service
                  ) || '-'}
                  ,
                </div>
              </div>
              <div className="claim-no-section">
                <div className="title">{'Claim Amount Submitted:'}</div>
                <div className="value">
                  {
                    <CurrencyFormatter
                      value={claimDetails?.total_claim_amount || '-'}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                      ignoreRound={true}
                      useSign={true}
                    />
                  }
                  ,
                </div>
              </div>
              <div className="claim-no-section">
                <div className="title">{'Paid Amount:'}</div>
                <div className="value">
                  {
                    <CurrencyFormatter
                      value={claimDetails?.total_amount_paid || '-'}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                      ignoreRound={true}
                      useSign={true}
                    />
                  }
                  ,
                </div>
              </div>
              <div className="claim-no-section">
                <div className="title">{'Patient Balance:'}</div>
                <div className="value">
                  {
                    <CurrencyFormatter
                      value={claimDetails?.patient_pay_amount || '-'}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'$'}
                      ignoreRound={true}
                      useSign={true}
                    />
                  }
                  ,
                </div>
              </div>
              <div className="claim-no-section">
                <div className="title">{'Paid Date:'}</div>
                <div className="value">
                  {HelperUtils.formatDisplayDate(claimDetails?.paid_date) ||
                    '-'}
                  ,
                </div>
              </div>
              <div className="claim-no-section">
                <div className="title">{'Claim Status:'}</div>
                <div className={`value ${getStatusClass(claimDetails.status)}`}>
                  {getStatusLabel(claimDetails.status)}
                </div>
              </div>
            </div>
          </div>
          {tab === 0 && renderIcdAndCptSection()}
        </div>
      </Drawer>
    );
  };

  return <div>{renderDrawer()}</div>;
};

export default ClaimDetails;
